import classNames from "classnames";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { ChangeEvent } from "react";
import { useSelector } from "react-redux";
import {
  useAddClarificationMutation,
  useGetOpenItemsQuery,
} from "store/apis/openItem";
import { RootState } from "store/store";
import { OpenItem } from "types/Models/openItem";
import { debounce } from "utils/debouncing";

type OpenItemClarificationInputProps = {
  openItem: OpenItem;
  label?: string;
};

export const OpenItemClarificationInput = ({
  openItem,
  label,
}: OpenItemClarificationInputProps) => {
  const { alertToast } = useToast();
  const [addClarification, { isLoading: isUpdating }] =
    useAddClarificationMutation();

  const { activeChannelGroupId: groupId } = useSelector(
    (state: RootState) => state.reviewAndBalancePayment
  );

  const { uuid, csv_id: csvId, clarification } = openItem;
  const hasLabel = Boolean(label);

  const { data: uploadedCSV } = useGetOpenItemsQuery(
    { csvId, groupId },
    { skip: !groupId || !csvId, refetchOnMountOrArgChange: true }
  );

  const { status } = uploadedCSV || {};

  const saveClarification = debounce(async (value: string) => {
    try {
      await addClarification({
        groupId,
        openItemId: uuid,
        clarification: value,
      }).unwrap();
    } catch (error) {
      alertToast({
        message:
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message ?? "Something went wrong",
      });
    }
  }, 1000);

  const handleClarificationChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    saveClarification(e.target.value);

  if (status === "SUBMITTED") {
    return (
      <div className="t-flex t-min-w-0 t-flex-col t-gap-1 t-text-body-sm">
        {label && <span className="t-text-text-60">{label}</span>}
        <span className="t-break-words t-text-text-100">
          {clarification || "-"}
        </span>
      </div>
    );
  }

  return (
    <FormikForm
      initialValues={{
        clarification: clarification || "",
      }}
      onSubmit={() => {}}
      className="all:unset"
    >
      <div
        className={classNames("t-flex t-gap-1.5", {
          "t-items-end": hasLabel,
          "t-items-center": !hasLabel,
        })}
      >
        <div className={classNames({ "t-min-w-0 t-flex-1": hasLabel })}>
          <TextArea
            name="clarification"
            label={label}
            required={hasLabel}
            placeholder={hasLabel ? "Enter" : undefined}
            block={hasLabel}
            onChange={handleClarificationChange}
            cols={100}
            rows={1}
          />
        </div>
        <span
          className={classNames("t-flex t-animate-spin", {
            "t-pb-2": hasLabel,
            "t-invisible": !Boolean(isUpdating),
            "t-block": Boolean(isUpdating),
          })}
        >
          <LoadingIcon />
        </span>
      </div>
    </FormikForm>
  );
};
